import {
  createProject,
  parseProject,
  projectMetrics,
  projectText,
  cameraPosition,
  MAX_CAMERAS,
  type ProjectDraft,
  type PlanCamera,
} from '~/utils/project';

export function useProjectPlanner() {
  const project = useState<ProjectDraft>('planner-project', createProject);
  const saveState = useState<'saved' | 'saving' | 'unavailable'>(
    'planner-save-state',
    () => 'saving',
  );
  const past = useState<ProjectDraft[]>('planner-history', () => []);
  const future = useState<ProjectDraft[]>('planner-future', () => []);
  const clone = (p: ProjectDraft) => JSON.parse(JSON.stringify(p)) as ProjectDraft;
  let lastEdit = { key: '', time: 0 };
  function commit(next: ProjectDraft, group = '') {
    const checked = parseProject(next);
    if (JSON.stringify(checked) === JSON.stringify(project.value)) return;
    if (!group || group !== lastEdit.key || Date.now() - lastEdit.time > 800)
      past.value = [...past.value.slice(-29), clone(project.value)];
    lastEdit = { key: group, time: Date.now() };
    future.value = [];
    project.value = checked;
  }
  function update(patch: Partial<ProjectDraft>, group = '') {
    commit({ ...clone(project.value), ...patch }, group);
  }
  function updateCamera(id: string, patch: Partial<PlanCamera>, group = '') {
    const camera = project.value.cameras.find((c) => c.id === id);
    if (patch.kind && camera && camera.kind !== patch.kind)
      patch = { ...patch, ...cameraPosition(patch.kind, camera.x, camera.y), productSlug: undefined };
    update(
      { cameras: project.value.cameras.map((c) => (c.id === id ? { ...c, ...patch } : c)) },
      group ? `${id}-${group}` : '',
    );
  }
  function addCamera(x = 0.5, y = 0.5, source?: PlanCamera) {
    if (project.value.cameras.length >= MAX_CAMERAS) return '';
    const id = `cam-${crypto.randomUUID()}`;
    const camera: PlanCamera = source
      ? {
          ...source,
          id,
          name: `${source.name.slice(0, 40)} (копия)`,
          ...cameraPosition(source.kind, source.x + 0.06, source.y + 0.06),
        }
      : {
          id,
          name: `Камера ${project.value.cameras.length + 1}`,
          kind: x < 0 || x > 1 || y < 0 || y > 1 ? 'outdoor' : 'indoor',
          ...cameraPosition(x < 0 || x > 1 || y < 0 || y > 1 ? 'outdoor' : 'indoor', x, y),
          angle: 90,
          fov: 90,
        };
    update({ cameras: [...project.value.cameras, camera] });
    return id;
  }
  function removeCamera(id: string) {
    update({ cameras: project.value.cameras.filter((c) => c.id !== id) });
  }
  function undo() {
    lastEdit.key = '';
    const previous = past.value.at(-1);
    if (!previous) return;
    future.value.push(clone(project.value));
    project.value = previous;
    past.value = past.value.slice(0, -1);
  }
  function redo() {
    lastEdit.key = '';
    const next = future.value.at(-1);
    if (!next) return;
    past.value.push(clone(project.value));
    project.value = next;
    future.value = future.value.slice(0, -1);
  }
  return {
    project,
    saveState,
    metrics: computed(() => projectMetrics(project.value)),
    text: () => projectText(project.value),
    update,
    updateCamera,
    addCamera,
    removeCamera,
    commit,
    undo,
    redo,
    canUndo: computed(() => !!past.value.length),
    canRedo: computed(() => !!future.value.length),
  };
}
